import { NetworkBasePopUp } from '../NetworkBasePopUp';
import { GzDialog } from '../../../../ui/gzDialog/component/GzDialog';
import { translateFn } from '../../../../utils/translator.helper';
import { ButtonColors, ButtonTypes } from '../../../../ui/gzButton/types';
import { NetworkPopUpType } from '../../network.pop-up.factory';
import { GzDeleteEntityWebComponent } from './component/GzDeleteEntityWebComponent';
import { DeleteEntityPopUpSpecifics, RemoveRecordsResponse } from './types';
import { DeleteActionState, Events } from '../../../networkAppContainer/helpers/types';
import { FooterButton } from '../NetworkPopUpFooterContentWebComponent';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { ConsoleRequest, DataProviderEntity, DataProviderType } from '../../../../utils/request';
import { GzToast, ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';

export class DeleteEntityPopUp extends NetworkBasePopUp<GzDeleteEntityWebComponent, DeleteEntityPopUpSpecifics> {
  protected extraDialogConfigs: Partial<GzDialog>;
  private type: NetworkPopUpType = NetworkPopUpType.DELETE_ENTITY;
  protected title: string = translateFn('Delete selected entities?');

  private isError = false;

  private deleteDialogButtonsConfig: Partial<FooterButton>[] = [
    {
      id: 'confirmButton',
      label: translateFn('DELETE'),
      type: ButtonTypes.OUTLINE,
      color: ButtonColors.NEGATIVE,
    },
    {
      id: 'cancelButton',
      label: translateFn('CANCEL'),
      type: ButtonTypes.OUTLINE,
      color: ButtonColors.SECONDARY,
    },
  ];

  protected customSetup(): void {
    this.popUpContent = new GzDeleteEntityWebComponent();

    if (!this.data.specifics) {
      return;
    }

    (this.popUpContent as GzDeleteEntityWebComponent).deletePopUpCases = this.data.specifics.states;

    this.customEventNameAfterConfirm = Events.DELETE_ENTITIES_SUCCESS;

    this.popUpFooterButtons = this.deleteDialogButtonsConfig;
  }

  protected async handleConfirmButtonClicked(event: CustomEvent) {
    if (this.isError) {
      return;
    }

    if (!this.data.specifics) {
      throw new Error('Missing specifics');
    }

    if (this.data.specifics.states[DeleteActionState.AD_ENTITIES_IN_SELECTED_ITEMS]) {
      return await this.handleDeleteActiveDirectoryEntities();
    }

    if (!this.data.targets) {
      throw new Error('Missing delete targets');
    }

    const records = this.data.targets.map((protectedEntity: ProtectedEntity) => {
      return {
        ...protectedEntity,
        updateAction: 0,
      };
    });

    const response: RemoveRecordsResponse = await ConsoleRequest.createRequest<RemoveRecordsResponse>({
      dataProviderType: DataProviderType.EPS,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'removeRecordsNirvana',
      payload: {
        records: records,
      },
    });

    const toastNotificationConfigs: GzToast = {
      message: translateFn('Entities successfully deleted.'),
      hasAction: false,
      severity: ToastSeverity.SUCCESS,
      keepOpen: false,
      autoCloseDelayMs: 10000,
    };

    if (!response.success) {
      toastNotificationConfigs.message = translateFn('Could not delete selected entities.');
      toastNotificationConfigs.severity = ToastSeverity.ERROR;
      GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
      return;
    }

    GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
    this.getCustomEventDetail = () => {
      return {
        records: records,
      };
    };
  }

  private async handleDeleteActiveDirectoryEntities() {
    if (!this.data.targets) {
      throw new Error('Missing delete targets');
    }

    const toastNotificationConfigs: GzToast = {
      message: translateFn('Deletion of integration entities has started successfully.'),
      hasAction: false,
      severity: ToastSeverity.SUCCESS,
      keepOpen: true,
    };

    try {
      const recordIds: Array<string> = this.data.targets.map((protectedEntity: ProtectedEntity) => protectedEntity._id);

      await ConsoleRequest.createRequest({
        dataProviderType: DataProviderType.EPS,
        dataProviderEntity: DataProviderEntity.MODEL,
        endpoint: 'ProtectedEntitiesEPS',
        method: 'deleteLicenseNirvana',
        payload: {
          protectedEntityIds: recordIds,
        },
      });

      GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
    } catch (error) {
      toastNotificationConfigs.message = translateFn('Could not start the deletion of integration entities.');
      toastNotificationConfigs.severity = ToastSeverity.ERROR;
      GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
    }
  }
}
